import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface ToastState {
  id: number;
  message: string;
  tone: 'success' | 'info';
}

interface ToastContextValue {
  showToast: (message: string, tone?: 'success' | 'info') => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => undefined });

export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const showToast = useCallback((message: string, tone: 'success' | 'info' = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, tone }]);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = window.setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, 2600);
    return () => window.clearTimeout(timer);
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-6 left-1/2 z-[80] flex w-[min(92vw,420px)] -translate-x-1/2 flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="animate-fade-up flex items-center gap-2.5 rounded-lg border border-background-200/70 bg-foreground-950 px-4 py-3 text-sm text-background-50"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
              <i className={`text-xs ${toast.tone === 'success' ? 'ri-check-line' : 'ri-information-line'}`}></i>
            </span>
            <span className="leading-snug">{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}